import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import AddressFinderRow from "./AddressFinderRow";
import AddressFinderFilter from "./AddressFinderFilter";
import { useIsMount, useLanguage } from "../../hooks";
import { getData } from "../../utils/requests";
import { Button } from "../Button";
import axios from "axios";
import {
    inputSettingsDefaults,
    columnLabelsDefaults,
    dataLabelsDefaults,
    filterLabelsDefaults
} from "../../constants/defaultProps";

import {
    DataGrid,
    DataGridRow,
    DataGridColumn,
    DataGridHead,
} from "../DataGrid";

import {
    InputForm
} from "../InputForm";
import parseFilters from "../../utils/parseFilters";
import dataVisibilityHelper from "../../utils/dataVisibilityHelper";

const FiltersBlock = ({filters, onFilterChange, filterLabels, language}) => {
    const [expand, setExpand] = useState(false);

    if (!filters || !filters.length) return null;

    return (
        <>
            {expand && (
                <div className="address-finder__filters">
                    {
                        filters.map((filter, idx)=>{
                            return (
                                <AddressFinderFilter
                                    language={language}
                                    key={idx}
                                    onFilterChange={onFilterChange}
                                    data={filter}
                                />
                            )
                        })
                    }
                </div>
            )}
            <Button
                type={"button"}
                size={'sm'}
                variant={'no-border px-0'}
                spanClass={'color-orange'}
                onClick={()=>setExpand(!expand)}
            >
                { expand ? filterLabels.collapse[language] : filterLabels.expand[language] }
            </Button>
        </>
    )
}

const componentData = {
    changeTimeout: 0,
    oldValue: '',
}

let cancelToken;

const AddressFinderTableHeading = ({
    columnLabels,
    visibleColumnLabels,
    language
}) => {
    return (
        <DataGridHead>
            <DataGridRow>
                {dataVisibilityHelper(visibleColumnLabels, "index") ? (
                    <DataGridColumn width={'5%'}>{columnLabels['id'][language]}</DataGridColumn>
                ) : ""}
                {dataVisibilityHelper(visibleColumnLabels, "address") ? (
                    <DataGridColumn width={'70%'}>{columnLabels['address'][language]}</DataGridColumn>
                ) : ""}
                {dataVisibilityHelper(visibleColumnLabels, "code") ? (
                    <DataGridColumn width={'15%'}>{columnLabels['code'][language]}</DataGridColumn>
                ) : ""}
                {dataVisibilityHelper(visibleColumnLabels, "selectButton") ? (
                    <DataGridColumn width={'10%'}>{" "}</DataGridColumn>
                ) : ""}
            </DataGridRow>
        </DataGridHead>
    )
}

const AddressFinder = ({
    columnLabels,
    dataLabels,
    filterLabels,
    apiBaseEndpointUri,
    highlightMatches,
    inputSettings,
    filters,
    onItemSelect,
    onInputChange,
    defaultOpenAllRows,
    showOnlyLowestLevelAddresses,
    showAdditionalFilters,
    visibleDataLabels,
    visibleColumnLabels,
    language,
    languageCookieName,
    defaultLanguage
}) => {
    const [data, setData] = useState([]);
    const [value, setValue] = useState('');
    const [loader, setLoader] = useState(false);
    const [queryParams, setQueryParams] = useState([]);
    const isMount = useIsMount();
    const [selectedItem, setSelectedItem] = useState({
        index: null,
        data: null
    });

    const [_language] = useLanguage(language, languageCookieName, defaultLanguage);

    const queryString = queryParams.map(e=>`&${e}`).join('');
    const endpointUri = `${apiBaseEndpointUri}/address-struct?search=${value}${queryString}`;

    const handleInputChange = (event) => {
        componentData.oldValue = value;
        setValue(event.target.value);
        if (onInputChange && typeof onInputChange === "function") {
            onInputChange(event)
        }
    }

    const filteredData = useMemo(()=>{
        if (showOnlyLowestLevelAddresses) {
            return data.filter(e => e.irAdrese === true);
        }

        return data;
    },[data, showOnlyLowestLevelAddresses])

    const handleValueChange = () => {
        // TODO: fix empty value issue
        // take attention to x/y filters (it should be run instantly without any value)

        // if (!value) {
        //     setData([])
        //     return;
        // }

        if (cancelToken) {
            cancelToken.cancel();
        }

        cancelToken = axios.CancelToken.source()

        setLoader(true);

        getData(endpointUri, {}, cancelToken.token)
            .then((res)=> {
                setData(res.data);
            })
            .catch((err) => {
                setData([]);
            })
            .finally(() => {
                // reset selected item
                setSelectedItem({
                    index: null,
                    data: null
                })
                setLoader(false);
            })
    }

    const _columnLabels = {
        ...columnLabelsDefaults,
        ...columnLabels,
    }

    const _dataLabels = {
        ...dataLabelsDefaults,
        ...dataLabels,
    }

    const _inputSettings = {
        ...inputSettingsDefaults,
        ...inputSettings,
    }

    const _filterLabels = {
        ...filterLabelsDefaults,
        ...filterLabels,
    }

    const onFilterChange = (filter, state, filterValue) => {
        if (filter.type === 'queryParam') {
            setQueryParams(prevState => {
                const newItems = [...prevState];

                const index = newItems.indexOf(filterValue);

                if (index === -1 && state) {
                    newItems.push(filterValue);
                } else {
                    newItems.splice(index, 1);
                }
                return newItems;
            })
        } else if (filter.type === 'queryParamWithInput') {
            if (!filterValue && !filter.instant) return

            setQueryParams(prevState => {
                const newItems = [...prevState],
                    newFilterValue = `${filter.queryParam}=${filterValue}`,
                    index = newItems.indexOf(newFilterValue);

                const regex = new RegExp(`^${filter.queryParam}=`),
                    oldIndex = newItems.findIndex(e=>e.match(regex))

                if (index === -1 && state) {
                    if(oldIndex !== -1) {
                        newItems.splice(oldIndex, 1)
                    }
                    newItems.push(newFilterValue);
                } else {
                    newItems.splice(index, 1);
                }
                return newItems;
            })
        }
    }

    const filterBlock = showAdditionalFilters ?
        <FiltersBlock
            language={_language}
            filters={parseFilters(filters)}
            filterLabels={_filterLabels}
            onFilterChange={onFilterChange}
        />
        : null;

    useEffect(() => {
        // if(value) {
            clearTimeout(componentData.changeTimeout);

            componentData.changeTimeout = setTimeout(() => {
                handleValueChange()
            },_inputSettings.requestTimeout || 200)
        // } else {
        //     setData([])
        // }
    },[value]);

    useEffect(() => {
        handleValueChange()
    },[queryParams]);

    useEffect(() => {
        if (!isMount) {
            if (onItemSelect && typeof onItemSelect === "function") {
                onItemSelect(selectedItem)
            }
        }
    }, [selectedItem])

    return (
        <div className="address-finder">
            <div className="my-3">
                <div style={{position:'relative'}}>
                    <>
                        { loader ? (
                            <div className="loader">
                                <i className="ic-loader"/>
                            </div>
                        ) : null }
                        <InputForm
                            label={_inputSettings.field && _inputSettings.field.label[_language]}
                            labelTop={true}
                            value={value}
                            placeholder={_inputSettings.field && _inputSettings.field.placeholder[_language]}
                            preventDefaultOnChange={true}
                            onChange={handleInputChange}
                        />

                        { filterBlock }
                    </>
                </div>
            </div>
            <DataGrid bordered>
                <AddressFinderTableHeading
                    visibleColumnLabels={visibleColumnLabels}
                    columnLabels={_columnLabels}
                    language={_language}
                />
                <tbody>
                    {filteredData.length ? (
                        filteredData.map((rowData, idx) => {
                            return (
                                <AddressFinderRow
                                    language={_language}
                                    visibleColumnLabels={visibleColumnLabels}
                                    visibleDataLabels={visibleDataLabels}
                                    enteredValue={value}
                                    dataLabels={_dataLabels}
                                    columnLabels={_columnLabels}
                                    data={rowData}
                                    key={rowData.code || idx}
                                    index={idx}
                                    highlightMatches={highlightMatches}
                                    defaultOpenAllRows={defaultOpenAllRows}
                                    selected={idx === selectedItem.index}
                                    onSelect={(itemID)=>{
                                        setSelectedItem({
                                            index: itemID,
                                            data: rowData
                                        });
                                    }}
                                />
                            )
                        })
                    ) : null}
                </tbody>
            </DataGrid>
        </div>
    )
}

AddressFinder.defaultProps = {
    defaultOpenAllRows: true,
    onItemSelect: null,
    onInputChange: null,
    highlightMatches: true,
    inputSettings: inputSettingsDefaults,
    columnLabels: columnLabelsDefaults,
    dataLabels: dataLabelsDefaults,
    filterLabels: filterLabelsDefaults,
    showOnlyLowestLevelAddresses: true,
    showAdditionalFilters: false,
    filters: [
        "city",
        "county",
        "parish",
        "village",
        'lks_lat',
        'lks_long'
    ],
    visibleColumnLabels: [
        "index",
        "address",
        "selectButton"
    ],
    visibleDataLabels: [
        "address",
        "zipCode"
    ],
    language: null,
    languageCookieName: "LV_language",
    defaultLanguage: "lv"
}

AddressFinder.propTypes = {
    defaultOpenAllRows: PropTypes.bool,
    onItemSelect: PropTypes.func,
    onInputChange: PropTypes.func,
    apiBaseEndpointUri: PropTypes.string.isRequired,
    highlightMatches: PropTypes.bool,
    inputSettings: PropTypes.shape({
        requestTimeout: PropTypes.number,
        field: PropTypes.shape({
            label: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
            }),
            placeholder: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
            }),
        }),
    }).isRequired,
    columnLabels: PropTypes.shape({
        id: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        address: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        code: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        select: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        selected: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
    }).isRequired,
    dataLabels: PropTypes.shape({
        code: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        address: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        zipCode: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        type: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        structure: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        history: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        city: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        county: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        parish: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        village: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        street: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        home: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        lksLat: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        lksLong: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired
    }).isRequired,
    filterLabels: PropTypes.shape({
        expand: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
        collapse: PropTypes.shape({
            ru: PropTypes.string,
            en: PropTypes.string,
            lv: PropTypes.string
        }).isRequired,
    }).isRequired,
    attributes: PropTypes.object,
    filters: PropTypes.arrayOf(
        PropTypes.oneOfType(
            [
                PropTypes.oneOf(['city', 'county', 'parish', 'village', 'limit', 'lks_lat', 'lks_long']),
                PropTypes.arrayOf(
                    PropTypes.oneOfType([
                        PropTypes.shape({
                            ru: PropTypes.string,
                            en: PropTypes.string,
                            lv: PropTypes.string
                        }),
                        PropTypes.shape({
                            type: PropTypes.string.isRequired,
                            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                            queryParam: PropTypes.string.isRequired,
                            instant: PropTypes.bool
                        })
                    ])
                )
            ]
        )
    ),
    showOnlyLowestLevelAddresses: PropTypes.bool,
    showAdditionalSearchParams: PropTypes.bool,
    visibleColumnLabels: PropTypes.arrayOf(
        PropTypes.oneOf([
            'index',
            'code',
            'address',
            'selectButton',
            'all'
        ]),
    ),
    visibleDataLabels: PropTypes.arrayOf(
        PropTypes.oneOf([
            'code',
            'address',
            'zipCode',
            'type',
            'structure',
            'history',
            'lksLat',
            'lksLong',
            'all'
        ]),
    ),
    language: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null])
    ]),
    defaultLanguage: PropTypes.string.isRequired
}

export default AddressFinder;